import React, { useEffect, useRef, useState } from 'react'

import api from '@disco/api/server'
import { CHARGEBEE_SCRIPT } from '@disco/constants/billing-provider-components'
import captureError from '@disco/utils/captureError'
import { getOrInitChargebee } from '@disco/utils/chargebee'
import useScript from '@disco/utils/use-script'

import type { ChargebeePricingPageSession } from 'types/subscription'

import BillingProviderLoadError from 'components/common/billing-provider-load-error'
import { Box, Flex, Link, Spinner, Text } from 'components/ds'
import ContactUsLink from 'components/ui/contact-us-link'

// the modal can't be dismissed, so a table that never draws must turn into a way out
const LOAD_TIMEOUT_MS = 15 * 1000
const SESSION_REFRESH_MARGIN_MS = 60 * 1000
const MAX_TIMEOUT_MS = 2 ** 31 - 1

type Status = 'loading' | 'ready' | 'error'

// the backend picks the table (plan family and currency) when it creates the session
// TODO(STELLA-36): take the session fetch as a prop so expired subscriptions can reuse this
const ChargebeePricingEmbed = (): React.ReactNode => {
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<Status>('loading')
  const [session, setSession] = useState<ChargebeePricingPageSession | null>(
    null,
  )
  const [attempt, setAttempt] = useState(0)

  const [scriptLoaded, scriptError] = useScript(CHARGEBEE_SCRIPT)

  useEffect(() => {
    let canceled = false
    const isRefresh = attempt > 0

    api.chargebeeSignupPricingForm().then(
      response => {
        if (!canceled) setSession(response)
      },
      error => {
        if (canceled) return
        // keep the table on screen; a refresh fails once the user has paid
        if (!isRefresh) setStatus('error')
        captureError(error, {
          moduleName: 'ChargebeePricingEmbed',
          description: 'Failed to load Chargebee pricing table session',
        })
      },
    )

    return () => {
      canceled = true
    }
  }, [attempt])

  useEffect(() => {
    if (scriptError) setStatus('error')
  }, [scriptError])

  useEffect(() => {
    const wrapper = tableWrapperRef.current
    if (!session || !scriptLoaded || scriptError || !wrapper) return
    let canceled = false

    // Chargebee's /loading frame counts as ready; the help line covers a table that never arrives
    // only from loading, so a late frame can't hide an error
    const observer = new MutationObserver(() =>
      setStatus(current => (current === 'loading' ? 'ready' : current)),
    )
    observer.observe(wrapper, { childList: true, subtree: true })

    const openPricingTable = async () => {
      const pricingTable = await getOrInitChargebee().pricingTable()
      if (canceled) return
      pricingTable.open({ pricingTable: () => Promise.resolve(session) })
    }

    openPricingTable().catch(error => {
      if (canceled) return
      setStatus('error')
      captureError(error, {
        moduleName: 'ChargebeePricingEmbed',
        description: 'Failed to open Chargebee pricing table',
      })
    })

    return () => {
      canceled = true
      observer.disconnect()
    }
  }, [session, scriptLoaded, scriptError])

  useEffect(() => {
    if (!session?.expires_at) return
    const delay =
      session.expires_at * 1000 - Date.now() - SESSION_REFRESH_MARGIN_MS
    // skewed device clock would refetch in a loop; setTimeout fires at once past its limit
    if (delay <= 0 || delay > MAX_TIMEOUT_MS) return
    // the modal stays open indefinitely, and an expired session stops the table working
    const timer = setTimeout(() => setAttempt(current => current + 1), delay)
    return () => clearTimeout(timer)
  }, [session])

  useEffect(() => {
    if (status !== 'loading') return
    const timer = setTimeout(() => setStatus('error'), LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [status])

  // chargebee.js keeps a failed pricing-table load for the page's life, so only a reload recovers every failure
  const retry = () => window.location.reload()

  return (
    <Box width="100%">
      {status === 'error' && <BillingProviderLoadError onRetry={retry} />}
      {status === 'loading' && (
        <Flex
          role="status"
          aria-label="Loading plans"
          align="center"
          justify="center"
          minHeight="600px"
        >
          <Spinner size="3" />
        </Flex>
      )}
      <Box
        ref={tableWrapperRef}
        id="chargebee-pricing-table"
        data-pricing-table-integration-type="api"
      />
      {status === 'ready' && (
        <Text as="p" size="2" color="gray" align="center" mt="3">
          Having trouble loading the plans?{' '}
          <Link asChild className="text-link">
            <button type="button" onClick={retry}>
              Retry
            </button>
          </Link>{' '}
          or{' '}
          <ContactUsLink
            className="text-link"
            intercomMsg="I’m having issues loading the subscription form"
          >
            contact us
          </ContactUsLink>
          .
        </Text>
      )}
    </Box>
  )
}

export default ChargebeePricingEmbed
